import { NextChange } from "./components/NextChange.js";
import { ProfilesPage } from "./pages/ProfilesPage.js";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { useDevices, useLogout, useMe } from "./api/hooks.js";
import { PageState } from "./components/PageState.js";
import { AudioConsentPage } from "./pages/AudioConsentPage.js";
import { AiNannyPage } from "./pages/AiNannyPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
import { DevicesPage } from "./pages/DevicesPage.js";
import { GameLibraryPage } from "./pages/GameLibraryPage.js";
import { LiveControlsPage } from "./pages/LiveControlsPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { SchedulesPage } from "./pages/SchedulesPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { UsageHistoryPage } from "./pages/UsageHistoryPage.js";
import { AlertsPage } from "./pages/AlertsPage.js";
import { SystemDiagnosticsPage } from "./pages/SystemDiagnosticsPage.js";
import { useEffect, useState } from "react";

const NAV = [
  { to: "/", label: "Overview" },
  { to: "/devices", label: "Devices" },
  { to: "/profiles", label: "Profiles" },
  { to: "/library", label: "Game Library" },
  { to: "/schedules", label: "Schedules" },
  { to: "/live", label: "Live Controls" },
  { to: "/usage", label: "Usage & History" },
  { to: "/audio", label: "Audio & Consent" },
  { to: "/nanny", label: "AI Nanny" },
  { to: "/alerts", label: "Alerts" },
  { to: "/system", label: "Diagnostics" },
  { to: "/settings", label: "Settings" },
];

export function App() {
  const me = useMe();
  const devices = useDevices(!!me.data);
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(
    () => localStorage.getItem("qg-theme") === "dark",
  );
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("qg-theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  const [deviceId, setDeviceId] = useState<string>("");

  if (me.isPending) return <p className="muted">Loading session…</p>;
  if (!me.data) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const recentDevice = [...(devices.data ?? [])].sort((a, b) =>
    (b.last_seen_at ?? "").localeCompare(a.last_seen_at ?? ""),
  )[0];
  const selected = devices.data?.some((d) => d.id === deviceId)
    ? deviceId
    : recentDevice?.id || "";
  const role = (me.data as { role: string }).role;

  return (
    <div className="app">
      <header className="topbar">
        <button
          className="menu-toggle secondary"
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          ☰
        </button>
        <div className="topbar-label">
          YOUR HOUSEHOLD <span>Device workspace</span>
        </div>
        <select
          aria-label="Select device"
          value={selected}
          onChange={(e) => setDeviceId(e.target.value)}
          disabled={!devices.data?.length}
        >
          {(devices.data ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.nickname}
            </option>
          ))}
          {!devices.data?.length ? (
            <option>
              {devices.isPending
                ? "Loading devices…"
                : devices.isError
                  ? "Unable to load devices"
                  : "No devices enrolled"}
            </option>
          ) : null}
        </select>
        <button
          className="secondary theme-toggle"
          aria-label={dark ? "Use light theme" : "Use dark theme"}
          onClick={() => setDark(!dark)}
        >
          {dark ? "Light mode" : "Dark mode"}
        </button>
        <span
          className="account-label"
          title={(me.data as { email: string }).email}
        >
          {role}
        </span>
        <button
          onClick={() => {
            logout.mutate();
            navigate("/login");
          }}
        >
          Log out
        </button>
      </header>
      <nav
        className={`nav ${menuOpen ? "is-open" : ""}`}
        aria-label="Dashboard sections"
      >
        <div className="brand">
          <span className="brand-mark">Q</span>
          <div>
            QuestGuardian<small>A little balance. More play.</small>
          </div>
        </div>
        <span className="nav-caption">WORKSPACE</span>
        {NAV.filter((n) =>
          [
            "/",
            "/live",
            "/profiles",
            "/library",
            "/schedules",
            "/devices",
            "/usage",
          ].includes(n.to),
        ).map((n) => (
          <RouteLink key={n.to} to={n.to} label={n.label} />
        ))}
        <details className="nav-more">
          <summary>More & settings</summary>
          {NAV.filter((n) =>
            ["/audio", "/nanny", "/alerts", "/system", "/settings"].includes(
              n.to,
            ),
          ).map((n) => (
            <RouteLink key={n.to} to={n.to} label={n.label} />
          ))}
          <button
            className="secondary"
            onClick={() => {
              logout.mutate();
              navigate("/login");
            }}
          >
            Log out
          </button>
        </details>
        <div className="nav-footer">
          <span className="status-dot" /> Your rules. Their adventures.
          <small>Manage playtime from anywhere.</small>
        </div>
      </nav>
      <main id="main-content">
        <div className="page-heading">
          <div>
            <p className="eyebrow">QUESTGUARDIAN / WORKSPACE</p>
            <h1>
              {NAV.find((n) => n.to === location.pathname)?.label ?? "Overview"}
            </h1>
            <p className="muted">
              {location.pathname === "/"
                ? "A clear view of playtime, boundaries, and your connected headset."
                : location.pathname === "/live"
                  ? "A little more time. A moment to pause. You’re in control."
                  : location.pathname === "/library"
                    ? "Choose what belongs in their next adventure."
                    : location.pathname === "/schedules"
                      ? "Build a rhythm that works for your household."
                      : "Everything you need, together in one place."}
            </p>
          </div>
          <span className="workspace-tag">Parent workspace</span>
        </div>
        <NextChange key={selected} deviceId={selected} />
        <PageState query={devices}>
          <Routes>
            <Route path="/" element={<DashboardPage deviceId={selected} />} />
            <Route
              path="/profiles"
              element={<ProfilesPage deviceId={selected} />}
            />
            <Route
              path="/devices"
              element={<DevicesPage deviceId={selected} />}
            />
            <Route
              path="/library"
              element={<GameLibraryPage deviceId={selected} />}
            />
            <Route
              path="/schedules"
              element={<SchedulesPage deviceId={selected} />}
            />
            <Route
              path="/live"
              element={<LiveControlsPage key={selected} deviceId={selected} />}
            />
            <Route
              path="/usage"
              element={<UsageHistoryPage deviceId={selected} />}
            />
            <Route
              path="/audio"
              element={<AudioConsentPage deviceId={selected} />}
            />
            <Route
              path="/nanny"
              element={<AiNannyPage deviceId={selected} />}
            />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/system" element={<SystemDiagnosticsPage />} />
            <Route path="/settings" element={<SettingsPage role={role} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PageState>
      </main>
    </div>
  );
}

function RouteLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) => (isActive ? "active" : "")}
    >
      <span className="nav-icon" aria-hidden="true">
        {
          (
            {
              "/": "◈",
              "/profiles": "◎",
              "/devices": "▣",
              "/library": "▦",
              "/schedules": "◷",
              "/live": "⏻",
              "/usage": "▥",
              "/audio": "♫",
              "/nanny": "✧",
              "/alerts": "◇",
              "/system": "⌘",
              "/settings": "⚙",
            } as Record<string, string>
          )[to]
        }
      </span>
      {label}
    </NavLink>
  );
}
